import { Host, h } from "@stencil/core";
import QrCreator from "qr-creator";
/**
 * @status developer-preview
 */
export class ChQr {
    constructor() {
        this.accessibleName = undefined;
        this.background = "white";
        this.errorCorrectionLevel = "High";
        this.fill = "black";
        this.radius = 0;
        this.size = 128;
        this.value = undefined;
    }
    #localKeyToDestroyPreviousQR = 0;
    el;
    #getColorValue = (color) => color?.includes("currentColor")
        ? color.replace("currentColor", getComputedStyle(this.el).color)
        : color;
    componentDidRender() {
        if (!this.value) {
            return;
        }
        QrCreator.render({
            text: this.value,
            radius: this.radius, // 0.0 to 0.5
            ecLevel: (this.errorCorrectionLevel ||
                "High")[0], // L, M, Q, H
            fill: this.#getColorValue(this.fill), // foreground color
            background: this.#getColorValue(this.background), // color or null for transparent
            size: this.size // in pixels
        }, 
        // Using a JSX' ref will give an error at runtime after the second re-render
        this.el.shadowRoot.querySelector("div"));
        this.#localKeyToDestroyPreviousQR++;
    }
    render() {
        return (h(Host, { key: 'c44d6ad5c118ab917ea9970eb5060191c721053a', role: this.value ? "img" : null, "aria-label": this.value ? this.accessibleName : null }, this.value && h("div", { key: this.#localKeyToDestroyPreviousQR })));
    }
    static get is() { return "ch-qr"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["qr.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["qr.css"]
        };
    }
    static get properties() {
        return {
            "accessibleName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies a short string, typically 1 to 3 words, that authors associate\nwith an element to provide users of assistive technologies with a label\nfor the element."
                },
                "attribute": "accessible-name",
                "reflect": false
            },
            "background": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The background color of the render QR. If not specified, \"transparent\"\nwill be used."
                },
                "attribute": "background",
                "reflect": false,
                "defaultValue": "\"white\""
            },
            "errorCorrectionLevel": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "ErrorCorrectionLevel",
                    "resolved": "\"High\" | \"Low\" | \"Medium\" | \"Quartile\"",
                    "references": {
                        "ErrorCorrectionLevel": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/qr/types.ts::ErrorCorrectionLevel"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The four values L, M, Q, and H will use %7, 15%, 25%, and 30% of the QR\ncode for error correction respectively. So on one hand the code will get\nbigger but chances are also higher that it will be read without errors\nlater on. This value is by default High (H)."
                },
                "attribute": "error-correction-level",
                "reflect": false,
                "defaultValue": "\"High\""
            },
            "fill": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "What color you want your QR code to be."
                },
                "attribute": "fill",
                "reflect": false,
                "defaultValue": "\"black\""
            },
            "radius": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Defines how round the blocks should be. Numbers from 0 (squares) to 0.5\n(maximum round) are supported."
                },
                "attribute": "radius",
                "reflect": false,
                "defaultValue": "0"
            },
            "size": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The total size of the final QR code in pixels."
                },
                "attribute": "size",
                "reflect": false,
                "defaultValue": "128"
            },
            "value": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string | undefined",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Any kind of text, also links, email addresses, any thing."
                },
                "attribute": "value",
                "reflect": false,
                "defaultValue": "undefined"
            }
        };
    }
    static get elementRef() { return "el"; }
}
